import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { Mail, Lock, User, Gift, Sparkles, Loader2, Layers, TrendingUp } from 'lucide-react';
import { Alert } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { useFingerprint } from '@/hooks/useFingerprint';
import { motion } from 'framer-motion';

// Google client ID from environment
const GOOGLE_CLIENT_ID = (import.meta as unknown as { env: Record<string, string> }).env.VITE_GOOGLE_CLIENT_ID || '';

// Signup credits constant (should match backend FREE_TIER_SIGNUP_CREDITS)
const SIGNUP_CREDITS = 150;

const VALUE_PROPS = [
  { icon: Layers, title: 'Multi-agent AI council', description: 'Multiple models debate to find the truth' },
  { icon: TrendingUp, title: 'Financial & strategic analysis', description: 'Deep-dive research across any domain' },
  { icon: Gift, title: `${SIGNUP_CREDITS} free credits`, description: 'No credit card required to start' },
];

export function SignupPage() {
  const navigate = useNavigate();
  const { register, loginWithGoogle } = useAuth();
  const { visitorId: fingerprintId } = useFingerprint();
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Google button responsive sizing (ported from LoginPage)
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const googleScriptLoadedRef = useRef(false);
  const lastRenderedWidthRef = useRef(0);

  const handleGoogleCallback = useCallback(async (response: { credential: string }) => {
    setError(null);
    setIsGoogleLoading(true);
    try {
      await loginWithGoogle(response.credential);
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-up failed');
    } finally {
      setIsGoogleLoading(false);
    }
  }, [loginWithGoogle, navigate]);

  const renderGoogleButton = useCallback(() => {
    const google = (window as unknown as { google?: { accounts: { id: { initialize: (config: { client_id: string; callback: (response: { credential: string }) => void }) => void; renderButton: (element: HTMLElement | null, config: { theme: string; size: string; width: number; shape: string; text: string }) => void } } } }).google;
    const container = googleButtonRef.current;
    if (!google || !container) return;

    const containerWidth = Math.floor(container.getBoundingClientRect().width);
    const buttonWidth = Math.max(200, Math.min(400, containerWidth));

    if (Math.abs(buttonWidth - lastRenderedWidthRef.current) < 2) return;
    lastRenderedWidthRef.current = buttonWidth;

    container.innerHTML = '';

    google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleCallback,
    });
    google.accounts.id.renderButton(
      container,
      {
        theme: 'filled_black',
        size: 'large',
        width: buttonWidth,
        shape: 'rectangular',
        text: 'signup_with',
      }
    );
  }, [handleGoogleCallback]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      googleScriptLoadedRef.current = true;
      renderGoogleButton();
    };
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [renderGoogleButton]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    const container = googleButtonRef.current;
    if (!container) return;

    let resizeTimeout: ReturnType<typeof setTimeout>;
    const observer = new ResizeObserver(() => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (googleScriptLoadedRef.current) {
          renderGoogleButton();
        }
      }, 150);
    });
    observer.observe(container);
    return () => {
      clearTimeout(resizeTimeout);
      observer.disconnect();
    };
  }, [renderGoogleButton]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!agreedToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);
    try {
      await register({
        email: formData.email,
        password: formData.password,
        first_name: formData.firstName || undefined,
        last_name: formData.lastName || undefined,
        fingerprint_id: fingerprintId || undefined,
      });
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Sign Up - Vettis"
        description="Create a Vettis account. Sign up with email or Google and get 150 free credits to start AI-powered research."
        path="/signup"
      />

      <div className="w-full max-w-4xl mx-auto grid md:grid-cols-[2fr_3fr] rounded-2xl overflow-hidden">
        {/* ── Left Panel: Brand Showcase ── */}
        <div className="hidden md:flex flex-col justify-center p-10 relative overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-verdict-primary/10 via-transparent to-verdict-primary/5" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-verdict-primary/8 blur-[100px] rounded-full" />

          <div className="relative z-10">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-8"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-verdict-primary/10 border border-verdict-primary/20 shadow-gold-md mb-6">
                <img src="/octoverdict-no-bg.png" alt="Vettis" className="w-9 h-9 object-contain" />
              </div>
              <h2 className="text-3xl font-bold font-serif tracking-tight text-text-primary mb-2">
                Research, Elevated
              </h2>
              <p className="text-text-secondary text-sm leading-relaxed">
                AI-powered research that goes beyond surface-level answers.
              </p>
            </motion.div>

            {/* Value Propositions */}
            <div className="space-y-5">
              {VALUE_PROPS.map((prop, i) => (
                <motion.div
                  key={prop.title}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 + i * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-verdict-primary/10 border border-verdict-primary/15 flex items-center justify-center mt-0.5">
                    <prop.icon className="w-4 h-4 text-verdict-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">{prop.title}</p>
                    <p className="text-xs text-text-muted mt-0.5">{prop.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Right Panel: Signup Form ── */}
        <div className="verdict-card rounded-2xl md:rounded-l-none p-5 sm:p-8 md:p-10 relative">
          {/* Ambient glow behind card */}
          <div className="absolute -inset-24 pointer-events-none rounded-full bg-verdict-primary/5 blur-[100px] md:hidden" />

          <div className="relative">
            {/* Header */}
            <div className="text-center mb-8">
              {/* Mobile-only logo */}
              <div className="md:hidden inline-flex items-center justify-center w-14 h-14 rounded-full bg-verdict-primary/10 border border-verdict-primary/20 mb-4 shadow-gold-md">
                <img src="/octoverdict-no-bg.png" alt="Vettis" className="w-8 h-8 object-contain" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-text-primary mb-1">Create your account</h2>
              <p className="text-text-secondary text-sm">
                Start researching with {SIGNUP_CREDITS} free credits
              </p>
            </div>

            {/* Credits Badge */}
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center gap-2 bg-verdict-primary/10 text-verdict-primary rounded-full border border-verdict-primary/20 px-4 py-1.5">
                <Gift className="w-4 h-4" />
                <span className="text-sm font-semibold">{SIGNUP_CREDITS} free credits included</span>
              </div>
            </div>

            {error && (
              <Alert variant="error" className="mb-6" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Google Sign-Up */}
            {GOOGLE_CLIENT_ID && (
              <>
                <div className="mb-6 relative">
                  {isGoogleLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center gap-2 py-3 rounded-lg bg-surface-light border border-border-subtle text-text-secondary text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Signing up with Google...
                    </div>
                  )}
                  <div
                    ref={googleButtonRef}
                    className={`w-full flex justify-center [&_iframe]:!w-full [&>div]:!w-full ${isGoogleLoading ? 'invisible' : ''}`}
                  />
                </div>

                {/* Divider */}
                <div className="relative mb-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border-subtle" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="px-4 py-0.5 bg-surface text-text-muted uppercase tracking-wider">or sign up with email</span>
                  </div>
                </div>
              </>
            )}

            {/* Email Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-text-primary mb-1.5">First Name</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="w-4 h-4 text-text-muted" />
                    </div>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      placeholder="John"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-2.5 bg-surface-light border border-border-subtle rounded-lg text-text-primary placeholder-text-muted text-sm focus:outline-none focus:border-verdict-primary/50 focus:ring-1 focus:ring-verdict-primary/30 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-text-primary mb-1.5">Last Name</label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-surface-light border border-border-subtle rounded-lg text-text-primary placeholder-text-muted text-sm focus:outline-none focus:border-verdict-primary/50 focus:ring-1 focus:ring-verdict-primary/30 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-1.5">Email</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="w-4 h-4 text-text-muted" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-2.5 bg-surface-light border border-border-subtle rounded-lg text-text-primary placeholder-text-muted text-sm focus:outline-none focus:border-verdict-primary/50 focus:ring-1 focus:ring-verdict-primary/30 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-text-primary mb-1.5">Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="w-4 h-4 text-text-muted" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Min. 8 characters"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-2.5 bg-surface-light border border-border-subtle rounded-lg text-text-primary placeholder-text-muted text-sm focus:outline-none focus:border-verdict-primary/50 focus:ring-1 focus:ring-verdict-primary/30 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-primary mb-1.5">Confirm Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="w-4 h-4 text-text-muted" />
                  </div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="Re-enter password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-2.5 bg-surface-light border border-border-subtle rounded-lg text-text-primary placeholder-text-muted text-sm focus:outline-none focus:border-verdict-primary/50 focus:ring-1 focus:ring-verdict-primary/30 transition-colors"
                  />
                </div>
              </div>

              {/* Terms and Privacy Policy Consent */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="agreeToTerms"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-border-subtle text-verdict-primary focus:ring-verdict-primary focus:ring-offset-0 bg-surface-light cursor-pointer"
                />
                <label htmlFor="agreeToTerms" className="text-xs text-text-muted cursor-pointer leading-relaxed">
                  I agree to the{' '}
                  <Link to="/terms" className="text-verdict-primary hover:text-verdict-primary-hover transition-colors" target="_blank">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-verdict-primary hover:text-verdict-primary-hover transition-colors" target="_blank">
                    Privacy Policy
                  </Link>
                  , including the use of AI to process my data and generate content.
                </label>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2.5 rounded-lg font-medium text-sm text-black bg-verdict-primary hover:bg-verdict-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Create Free Account
                  </>
                )}
              </button>
            </form>

            <p className="text-center text-text-muted text-sm mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-verdict-primary hover:text-verdict-primary-hover transition-colors">
                Log in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
